import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import auth from '../utils/auth';
import { getUserProfile } from '../utils/api_dashboard';
import { getRegister } from '../utils/api_integrations';
import { INTEGRATIONS_LOGO_PATH } from '../components/common/constants';
import Sidebar from '../components/dash/Sidebar';
import Navbar from '../components/dash/Navbar';
import FloatingNavToggle from '../components/dash/FloatingNavToggle';
import RegisterGrid from '../components/integrations/RegisterGrid';
import WizardFrame from '../components/integrations/WizardFrame';
import PmsWizard from '../components/integrations/PmsWizard';
import GoogleWizard from '../components/integrations/GoogleWizard';
import MetaWizard from '../components/integrations/MetaWizard';
import XeroWizard from '../components/integrations/XeroWizard';
import MyobWizard from '../components/integrations/MyobWizard';

// ---------------------------------------------------------------- #
// IntegrationsPage
// ---------------------------------------------------------------- #
//
// The Integrations Register page at /integrations, for Entity Admins.
// The shell (navbar, sidebar, profile fetch, 401 handling) is copied from
// MyProfile.jsx. Steps, in the order they run:
//   1. Wait for the profile, so isEntityAdmin is known.
//   2. Gate: anyone who is not an Entity Admin sees a short card only.
//   3. Return from OAuth: read the query string, show a banner, clear it,
//      and open that provider's wizard at step 2 after a connect.
//   4. Load the grid from GET register.
//   5. Open a wizard when a cell is clicked; after a save, reload the grid
//      and tell the sidebar widget to refresh.

// ---------------------------------------------------------------- #
// Constants
// ---------------------------------------------------------------- #

/** Event the sidebar widget listens for to reload its counts. */
const INTEGRATIONS_CHANGED_EVENT = 'parki:integrations-changed';

/** Provider names used in banner text. */
const PROVIDER_NAMES = {
  xero: 'Xero',
  myob: 'MYOB',
  google: 'Google',
  meta: 'Facebook'
};

/** Banner text for each OAuth error reason that reads the same for every provider. */
const OAUTH_REASON_MESSAGES = {
  'state-invalid': 'That sign-in link has expired. Please click Connect again.',
  'cancelled': 'You cancelled the sign-in. Nothing was changed.',
  'not-allowed': "Only your organisation's admins can connect accounts.",
  'no-organisation': "Xero didn't return an organisation. Please try again and tick one organisation.",
  'tenant-belongs-to-another-account': 'This Xero organisation is already connected to another Parki account. Contact Parki support.',
  'no-company-file': "MYOB didn't say which company file you picked. Please try again.",
  'file-belongs-to-another-account': 'This company file is already connected to another Parki account. Contact Parki support.',
  'myob-user-belongs-to-another-account': 'Your MYOB login is already connected to another Parki account. Contact Parki support.',
  'no-refresh-token': "Google didn't give Parki ongoing access. Remove Parki at myaccount.google.com/permissions and connect again.",
  'not-built': 'This connection is not available yet.'
};

/** Banner text for an error reason this page does not know. */
const OAUTH_UNKNOWN_ERROR_MESSAGE = 'The connection did not finish. Please try again.';

/** Which wizard each grid product opens (bookkeeping is decided per park). */
const PRODUCT_PROVIDERS = {
  pms: 'pms',
  analytics: 'google',
  business: 'google',
  facebook: 'meta',
  instagram: 'meta'
};

/** The two bookkeeping systems offered when a park has none yet. */
const BOOKKEEPING_OPTIONS = [
  { provider: 'xero', label: 'Xero', logo: `${INTEGRATIONS_LOGO_PATH}/xero.png` },
  { provider: 'myob', label: 'MYOB', logo: `${INTEGRATIONS_LOGO_PATH}/myob.png` }
];

// ---------------------------------------------------------------- #
// Helpers
// ---------------------------------------------------------------- #

/**
 * Treat true, 1 and "1" as yes, because the profile API may send the flag as any of them.
 */
const isYesFlag = (value) => {
  return value === true || value === 1 || value === '1';
};

/**
 * Read the OAuth return parameters from the query string, or null when there are none.
 */
const readOAuthReturn = (search) => {
  // Nothing to read without provider and result
  const params = new URLSearchParams(search);
  const provider = params.get('provider');
  const result = params.get('result');
  const hasReturn = Boolean(provider) && Boolean(result);
  if (!hasReturn) {
    return null;
  }

  return {
    provider: provider,
    result: result,
    reason: params.get('reason'),
    tenant: params.get('tenant'),
    file: params.get('file'),
    skipped: params.get('skipped')
  };
};

/**
 * Build the banner (colour and text) for an OAuth return.
 */
const buildOAuthBanner = (oauthReturn) => {
  const providerName = PROVIDER_NAMES[oauthReturn.provider] || 'The provider';

  // Successful connects, one message per provider
  if (oauthReturn.result === 'connected') {
    let text = 'Connected.';
    if (oauthReturn.provider === 'xero') {
      text = `Connected ${oauthReturn.tenant || 'Xero'}.`;
      if (oauthReturn.skipped) {
        text += ` Xero connects one organisation at a time, so click Connect again for ${oauthReturn.skipped}.`;
      }
    } else if (oauthReturn.provider === 'myob') {
      text = `Connected ${oauthReturn.file || 'MYOB'}.`;
    } else if (oauthReturn.provider === 'google') {
      text = 'Connected Google.';
    } else if (oauthReturn.provider === 'meta') {
      text = 'Connected Facebook and Instagram.';
    }
    return { colour: 'success', text: text };
  }

  // A provider refusal names the provider
  if (oauthReturn.reason === 'provider-error') {
    return { colour: 'danger', text: `${providerName} refused the connection. Please try again.` };
  }

  // Every other reason has fixed text
  const reasonText = OAUTH_REASON_MESSAGES[oauthReturn.reason];
  if (reasonText) {
    return { colour: 'danger', text: reasonText };
  }

  return { colour: 'danger', text: OAUTH_UNKNOWN_ERROR_MESSAGE };
};

/**
 * Find the connection key (Xero bdoXeroId or MYOB myobId) the OAuth return names, or null.
 */
const findReturnedConnectionKey = (register, oauthReturn) => {
  // Xero returns the organisation name
  if (oauthReturn.provider === 'xero' && oauthReturn.tenant) {
    for (const tenant of register?.connections?.xero || []) {
      if (tenant.tenantName === oauthReturn.tenant) {
        return String(tenant.bdoXeroId);
      }
    }
  }

  // MYOB returns the company file name
  if (oauthReturn.provider === 'myob' && oauthReturn.file) {
    for (const file of register?.connections?.myob || []) {
      if (file.companyName === oauthReturn.file) {
        return file.myobId;
      }
    }
  }

  return null;
};

// ---------------------------------------------------------------- #
// The page
// ---------------------------------------------------------------- #

/**
 * The Integrations Register page.
 */
const IntegrationsPage = () => {
  const history = useHistory();
  const location = useLocation();

  // ---------------------------------------------------------------- #
  // Shell state (as MyProfile)
  // ---------------------------------------------------------------- #

  const [user, setUser] = useState(auth.getCurrentUser() || {});
  const [pageLoading, setPageLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [anomalyCount, setAnomalyCount] = useState({});
  const [customLogo, setCustomLogo] = useState({ hasCustomLogo: false, logo_path: '', logo_thumb_path: '' });
  const [navbarVisible, setNavbarVisible] = useState(() =>
    localStorage.getItem('navbarVisible') !== 'false' // Default to visible
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() =>
    localStorage.getItem('sidebarCollapsed') === 'true'
  );

  // ---------------------------------------------------------------- #
  // Register state
  // ---------------------------------------------------------------- #

  const [register, setRegister] = useState(null);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState(null);
  const [oauthReturn] = useState(() => readOAuthReturn(location.search));
  const [banner, setBanner] = useState(() => {
    // Only a return from OAuth shows a banner
    if (!oauthReturn) {
      return null;
    }
    return buildOAuthBanner(oauthReturn);
  });
  const [pendingWizardProvider, setPendingWizardProvider] = useState(() => {
    // Only a successful connect opens a wizard at step 2
    const connected = oauthReturn && oauthReturn.result === 'connected';
    if (!connected) {
      return null;
    }
    return oauthReturn.provider;
  });
  const [openWizard, setOpenWizard] = useState(null);
  const [bookkeepingChooser, setBookkeepingChooser] = useState(null);

  // ---------------------------------------------------------------- #
  // Shell: navbar and sidebar toggles
  // ---------------------------------------------------------------- #

  // Toggle functions for navbar and sidebar
  const toggleNavbar = () => {
    setNavbarVisible(prev => {
      const newState = !prev;
      localStorage.setItem('navbarVisible', newState);
      return newState;
    });
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => !prev);
  };

  // Set page title
  useEffect(() => {
    document.title = 'Parki | Integrations Register';
  }, []);

  // Send report clicks in the sidebar to the dashboard, as MyProfile does
  const handleReportSelect = (report) => {
    localStorage.setItem('selectedReport', JSON.stringify(report));
    history.push('/dashboard');
  };

  // ---------------------------------------------------------------- #
  // Step 1: wait for the profile
  // ---------------------------------------------------------------- #

  // Fetch user profile data and reports for sidebar
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check if user is authenticated
        if (!auth.isAuthenticated()) {
          history.push('/login');
          return;
        }

        // Fetch user profile
        const profileData = await getUserProfile();

        // build user data variable
        const tmpUser = {
          firstName: profileData?.user?.given_name,
          lastName: profileData?.user?.family_name,
          userType: profileData?.userData?.userType || 'customer',
          entityName: profileData?.userData?.entityName || 'unknown',
          entityId: parseInt(profileData?.userData?.linkedEntity) || null,
          isEntityAdmin: profileData?.userData?.isEntityAdmin || false,
          userId: profileData?.userData?.userId || null,
          email: profileData?.user?.email || '',
          defaultTV: profileData?.userData?.DefaultTV || -1
        };

        // update user data in local storage
        auth.setCurrentUser(tmpUser);

        // Update user state with profile data
        setUser(tmpUser);

        // set the anomaly count
        setAnomalyCount(profileData?.anomaly_count || {});

        // set custom logo info
        setCustomLogo({
          hasCustomLogo: profileData?.custom_logo?.hasCustomLogo || false,
          logo_path: profileData?.custom_logo?.logo_path || '',
          logo_thumb_path: profileData?.custom_logo?.logo_thumb_path || ''
        });

        // Store reports data for sidebar
        if (profileData?.reports && profileData.reports.length > 0) {
          setReports(profileData.reports);
        }

        setPageLoading(false);
      } catch (error) {
        console.error('Error fetching user profile:', error);
        setPageLoading(false);

        // If unauthorized (401), redirect to login
        if (error.response && error.response.status === 401) {
          auth.logout();
          history.push('/login');
        }
      }
    };
    fetchData();
  }, [history]);

  // ---------------------------------------------------------------- #
  // Step 2: the Entity Admin gate
  // ---------------------------------------------------------------- #

  // Only Entity Admins see the register
  const isEntityAdmin = isYesFlag(user.isEntityAdmin);

  // Connect is blocked while staff view as the customer (the API refuses too)
  const isImpersonating = localStorage.getItem('isImpersonating') === 'true';
  const viewerCanConnect = register?.viewer?.canConnect !== false;
  const canConnect = viewerCanConnect && !isImpersonating;

  // ---------------------------------------------------------------- #
  // Step 3: return from OAuth
  // ---------------------------------------------------------------- #

  // Remove the query string so a refresh does not show the banner again
  useEffect(() => {
    if (oauthReturn) {
      history.replace('/integrations');
    }
  }, [oauthReturn, history]);

  // After a connect, open that provider's wizard at step 2 once the grid has loaded
  useEffect(() => {
    if (!pendingWizardProvider || !register) {
      return;
    }

    // Find the connection the provider named, for Xero and MYOB
    const connectionKey = findReturnedConnectionKey(register, oauthReturn);

    setOpenWizard({
      provider: pendingWizardProvider,
      parkiId: null,
      initialStep: 2,
      connectionKey: connectionKey
    });
    setPendingWizardProvider(null);
  }, [pendingWizardProvider, register, oauthReturn]);

  // ---------------------------------------------------------------- #
  // Step 4: load the grid
  // ---------------------------------------------------------------- #

  /**
   * Load the register grid from the API.
   */
  const loadRegister = async () => {
    setRegisterLoading(true);
    setRegisterError(null);
    try {
      const result = await getRegister();
      setRegister(result.data);
    } catch (err) {
      // An expired sign-in goes back to the login page
      if (err.httpStatus === 401) {
        auth.logout();
        history.push('/login');
        return;
      }
      setRegisterError(err.message || 'The Integrations Register could not be loaded.');
    } finally {
      setRegisterLoading(false);
    }
  };

  // Load the grid once the profile says this user is an Entity Admin
  useEffect(() => {
    if (pageLoading || !isEntityAdmin) {
      return;
    }
    loadRegister();
    // loadRegister only uses setters and history, so it is safe to leave out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLoading, isEntityAdmin]);

  // ---------------------------------------------------------------- #
  // Step 5: open and close wizards
  // ---------------------------------------------------------------- #

  /**
   * Reload the grid and tell the sidebar widget that something changed.
   */
  const announceChange = () => {
    loadRegister();
    window.dispatchEvent(new Event(INTEGRATIONS_CHANGED_EVENT));
  };

  /**
   * Open the wizard for the product a park's cell belongs to.
   */
  const handleCellClick = (park, productKey, cell) => {
    // Every product except bookkeeping always opens the same wizard
    const fixedProvider = PRODUCT_PROVIDERS[productKey];
    if (fixedProvider) {
      setOpenWizard({ provider: fixedProvider, parkiId: park.parkiId, initialStep: 1, connectionKey: null });
      return;
    }

    // Bookkeeping that is already linked opens the provider it is linked to
    const links = cell.mapping?.links || [];
    if (links.length > 0) {
      const firstLink = links[0];
      setOpenWizard({ provider: firstLink.provider, parkiId: park.parkiId, initialStep: 1, connectionKey: firstLink.connectionKey });
      return;
    }

    // Otherwise ask which bookkeeping system the park uses
    setBookkeepingChooser({ park: park, cell: cell, notNeededChanged: false });
  };

  /**
   * Open the wizard a grid warning's "Fix" points at.
   */
  const handleWarningFix = (warning) => {
    // Xero and MYOB warnings name their connection
    if (warning.provider === 'xero' || warning.provider === 'myob') {
      setOpenWizard({ provider: warning.provider, parkiId: warning.parkiId, initialStep: 1, connectionKey: warning.connectionKey });
      return;
    }

    // A park linked to both systems opens the first system it is linked to
    let provider = 'xero';
    let connectionKey = null;
    for (const park of register?.parks || []) {
      if (park.parkiId !== warning.parkiId) {
        continue;
      }
      const links = park.cells?.bookkeeping?.mapping?.links || [];
      if (links.length > 0) {
        provider = links[0].provider;
        connectionKey = links[0].connectionKey;
      }
    }
    setOpenWizard({ provider: provider, parkiId: warning.parkiId, initialStep: 1, connectionKey: connectionKey });
  };

  /**
   * Close a wizard; reload when it saved something.
   */
  const handleWizardClose = (changed) => {
    setOpenWizard(null);
    if (changed) {
      announceChange();
    }
  };

  // ---------------------------------------------------------------- #
  // Bookkeeping chooser
  // ---------------------------------------------------------------- #

  /**
   * Open the Xero or MYOB wizard for the park in the chooser.
   */
  const handleChooseBookkeeping = (provider) => {
    const parkiId = bookkeepingChooser.park.parkiId;
    const notNeededChanged = bookkeepingChooser.notNeededChanged;
    setBookkeepingChooser(null);
    setOpenWizard({ provider: provider, parkiId: parkiId, initialStep: 1, connectionKey: null });

    // Refresh the grid if the not-needed switch was used before choosing
    if (notNeededChanged) {
      announceChange();
    }
  };

  /**
   * Remember that the footer "Not needed" switch saved a change, so closing refreshes the grid.
   */
  const handleBookkeepingNotNeededChange = () => {
    setBookkeepingChooser((current) => {
      return { ...current, notNeededChanged: true };
    });
  };

  /**
   * Close the chooser, refreshing the grid if the not-needed switch changed anything.
   */
  const handleChooserClose = () => {
    const notNeededChanged = bookkeepingChooser.notNeededChanged;
    setBookkeepingChooser(null);
    if (notNeededChanged) {
      announceChange();
    }
  };

  /**
   * Draw the "which bookkeeping system" chooser. It uses the same footer "Not needed" switch as the other wizards.
   */
  const renderBookkeepingChooser = () => {
    const park = bookkeepingChooser.park;
    const cell = bookkeepingChooser.cell;

    // One tile per bookkeeping system
    const tiles = [];
    for (const option of BOOKKEEPING_OPTIONS) {
      tiles.push(
        <div key={option.provider} className="col-sm-6">
          <button type="button" className="integrations-provider-tile" onClick={() => handleChooseBookkeeping(option.provider)}>
            <img src={option.logo} alt={option.label} />
            <span className="mt-2 fw-medium">{option.label}</span>
          </button>
        </div>
      );
    }

    return (
      <WizardFrame
        title="Bookkeeping"
        register={register}
        parkiId={park.parkiId}
        products={['bookkeeping']}
        onNotNeededChange={handleBookkeepingNotNeededChange}
        steps={[]}
        onClose={handleChooserClose}
      >
        <p className="text-center text-body-secondary mb-4">Which bookkeeping system does this park use?</p>

        {/* Explain a legacy No Integration status set by staff */}
        {cell.reason && (
          <div className="alert alert-secondary" role="status">{cell.reason}</div>
        )}

        <div className="row g-3">
          {tiles}
        </div>
      </WizardFrame>
    );
  };

  // ---------------------------------------------------------------- #
  // Render: the open wizard
  // ---------------------------------------------------------------- #

  /**
   * Draw whichever provider wizard is open, or nothing.
   */
  const renderOpenWizard = () => {
    if (!openWizard || !register) {
      return null;
    }

    // The props every provider wizard takes
    const wizardProps = {
      register: register,
      parkiId: openWizard.parkiId,
      initialStep: openWizard.initialStep,
      connectionKey: openWizard.connectionKey,
      canConnect: canConnect,
      onClose: handleWizardClose
    };

    // Pick the wizard for the provider
    if (openWizard.provider === 'pms') {
      return <PmsWizard {...wizardProps} />;
    }
    if (openWizard.provider === 'google') {
      return <GoogleWizard {...wizardProps} />;
    }
    if (openWizard.provider === 'meta') {
      return <MetaWizard {...wizardProps} />;
    }
    if (openWizard.provider === 'xero') {
      return <XeroWizard {...wizardProps} />;
    }
    if (openWizard.provider === 'myob') {
      return <MyobWizard {...wizardProps} />;
    }
    return null;
  };

  // ---------------------------------------------------------------- #
  // Render: page content
  // ---------------------------------------------------------------- #

  /**
   * Draw the gate card, or the banner, warnings and grid.
   */
  const renderContent = () => {
    // Not an Entity Admin: a short card and nothing else
    if (!isEntityAdmin) {
      return (
        <div className="card">
          <div className="card-body integrations-plain-card-body">
            The Integrations Register is for your organisation's admins. Ask your Parki admin to add you.
          </div>
        </div>
      );
    }

    // Read the warnings the API raised
    const warnings = register?.warnings || [];

    // The parks this admin can open, so a warning about a park outside their list asks for support instead of "Fix"
    const ownParkKeys = new Set();
    for (const park of register?.parks || []) {
      ownParkKeys.add(String(park.parkiId));
    }

    return (
      <>
        {/* Return from OAuth */}
        {banner && (
          <div className={`alert alert-${banner.colour} alert-dismissible`} role="status">
            {banner.text}
            <button type="button" className="btn-close" aria-label="Close" onClick={() => setBanner(null)}></button>
          </div>
        )}

        {/* Staff viewing as the customer */}
        {isImpersonating && (
          <div className="alert alert-info" role="status">
            You are viewing as the customer. You can change mappings, but connecting accounts is turned off. Ask the customer to connect their own account.
          </div>
        )}

        {/* Grid load error */}
        {registerError && (
          <div className="alert alert-danger" role="alert">
            {registerError}
            <button type="button" className="btn btn-sm btn-label-danger ms-3" onClick={loadRegister}>Try again</button>
          </div>
        )}

        {/* Connections that need attention */}
        {warnings.length > 0 && (
          <div className="integrations-warnings mb-4">
            {warnings.map((warning, index) => (
              <div key={`${warning.provider}-${warning.connectionKey}-${warning.parkiId}-${index}`} className="alert alert-warning d-flex align-items-center justify-content-between gap-2" role="status">
                <span>
                  <i className="icon-base ti tabler-alert-triangle me-1"></i>
                  {warning.message}
                </span>
                {ownParkKeys.has(String(warning.parkiId)) && (
                  <button type="button" className="btn btn-sm btn-warning flex-shrink-0" onClick={() => handleWarningFix(warning)}>Fix</button>
                )}
                {!ownParkKeys.has(String(warning.parkiId)) && (
                  <span className="small flex-shrink-0">Contact Parki support to change this park</span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* The grid, or a spinner while it first loads */}
        {register ? (
          <RegisterGrid register={register} onCellClick={handleCellClick} />
        ) : (
          registerLoading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )
        )}
      </>
    );
  };

  // ---------------------------------------------------------------- #
  // Render: the shell
  // ---------------------------------------------------------------- #

  // Show the loader until the profile says whether the user is an Entity Admin
  if (pageLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Heading names the entity once the register has loaded
  const entityName = register?.entity?.name || '';

  return (
    <div id="dashboard" className={`layout-wrapper layout-content-navbar ${!navbarVisible ? 'layout-without-navbar' : ''}`}>
      <div className="layout-container">
        {/* Sidebar component */}
        <Sidebar
          activeItem="integrations"
          collapsed={sidebarCollapsed}
          toggleSidebar={toggleSidebar}
          reports={reports}
          selectedReport={null}
          onSelectReport={handleReportSelect}
          anomalyAlerts={anomalyCount}
          customLogo={customLogo}
          user={user}
        />

        {/* Layout page */}
        <div className="layout-page">
          {/* Navbar component */}
          {navbarVisible && (
            <Navbar
              user={user}
              toggleSidebar={toggleSidebar}
              selectedReport={null}
              sidebarCollapsed={sidebarCollapsed}
            />
          )}

          {/* Content */}
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <h4 className="fw-bold py-3 mb-4">
                Integrations Register
                {entityName && (
                  <span className="text-body-secondary fw-normal fs-6 ms-2">{entityName}</span>
                )}
              </h4>

              {renderContent()}
            </div>
          </div>
        </div>
      </div>

      {/* Wizards */}
      {bookkeepingChooser && renderBookkeepingChooser()}
      {renderOpenWizard()}

      {/* Overlay */}
      <div className="layout-overlay layout-menu-toggle"></div>

      {/* Drag Target for Mobile */}
      <div className="drag-target"></div>

      <FloatingNavToggle visible={navbarVisible} onToggle={toggleNavbar} offset={0} />
    </div>
  );
};

export default IntegrationsPage;
